import React, { createContext, useContext } from 'react'
import { useCandidate, queryClient } from './CandidateProvider'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { cn } from '~/lib/utils'
import moment from 'moment'
import UploadResumeRevision from './UploadResumeRevision'
import RevisionDialog from './RevisionDialog'
import { useServiceRequests } from './ServicesSection'
import NoShowDialog from './NoShowDialog'
import RescheduleMeetingDialog from './RescheduleMeetingDialog'
import TabNav from '../shared/TabNav/TabNav'
import ButtonContainer from '../shared/TabNav/ButtonContainer'
import TabButton from '../shared/TabNav/TabButton'
import { PanelContainer, TabPanel } from '../shared/TabNav/index'
import useIsActionable from './useIsActionable'
import CancelMeetingModal from './CancelMeetingModal'
import { RequestCell } from './ServiceRequestDetail'
import { InitiateServiceRequestDialog } from './InitiateServiceRequestForm'
import { Button } from '~/lib/ui/button'
import { useFormatTime } from '~/hooks/useFormatTime'
import CoachChangeFormatDialog from './CoachChangeFormatDialog'

function adjustDueAtToWeekday(dueAt) {
  if (!dueAt) return dueAt
  let date = moment(dueAt)
  // 0 = Sunday, 6 = Saturday
  if (date.day() === 6) {
    date = date.add(2, 'days')
  } else if (date.day() === 0) {
    date = date.add(1, 'days')
  }
  return date
}

const ResumeReviewContext = createContext()
const useResumeReview = () => useContext(ResumeReviewContext) || {}
export { useResumeReview }

const ResumeReviewDetail = () => {
  const { detailItemId, detailType } = useCandidate()
  const { serviceRequests } = useServiceRequests()
  const currentResumeReview =
    serviceRequests?.find(
      (serviceRequest) => serviceRequest.id == detailItemId,
    ) ||
    serviceRequests?.find(
      (serviceRequest) =>
        serviceRequest.requestType == `ServiceRequest::${detailType}`,
    )

  return (
    <ResumeReviewContext.Provider value={currentResumeReview}>
      <TabNav defaultActiveTab="resume_review_progress">
        <ButtonContainer className="justify-start gap-4">
          <TabButton
            name="resume_review_progress"
            text="Resume Review Progress"
          />
          <StatusIndicator />
        </ButtonContainer>
        <PanelContainer className={'mt-6'}>
          <TabPanel name="resume_review_progress">
            <div className="my-4 text-lg">
              Flex Resume Reviews include an initial edit, which may be
              completed electronically or during your first meeting. The service
              also provides a second round of revisions and a final closing
              review to ensure the resume is polished and ready to use. Please
              note that each time you submit a revision, a document must be
              submitted.
            </div>
            <DetailTable />
          </TabPanel>
        </PanelContainer>
      </TabNav>
    </ResumeReviewContext.Provider>
  )
}
export default ResumeReviewDetail

const DetailTable = () => {
  return (
    <Table
      className="w-full border-separate"
      tableClass="rounded-2xl border border-silver"
    >
      <ReviewHeading />
      <TableBody>
        <CreatedAtRow />
        <AssignmentRow />
        <ReviewFormatRow />
        <PreWorkRow />
        <NoShowRows idx={0} />
        <InitialMeetingRow />
        <FirstRevisionRow />
        <NoShowRows idx={1} />
        <SecondMeetingRow revisionNumber={0} />
        <SecondRevisionRow />
        <FinalRevisionRow />
      </TableBody>
    </Table>
  )
}

export const isActionable = (
  status,
  meetingViaCalendly,
  revisionNumber,
  displayStatus = null,
) => {
  const noShowOne =
    displayStatus === 'Assigned to Coach' && status === 'scheduled'
  const noShowTwo =
    displayStatus === 'Awaiting Candidate Response' &&
    status == 'revision_meeting_requested'
  if (revisionNumber === 2) {
    return true
  }
  if (noShowOne || noShowTwo) {
    return false
  }
  return (
    [
      'revision_requested',
      'scheduled',
      'revision_meeting_requested',
      'final_revision_returned',
      'revision_declined',
    ].includes(status) ||
    (!meetingViaCalendly && status === 'assigned')
  )
}

export const StatusIndicator = ({ serviceRequest }) => {
  // If serviceRequest is passed, use its fields; otherwise, use context
  const context = useResumeReview()
  const { status, displayStatus, completedAt, meetingViaCalendly } =
    serviceRequest || context

  return (
    <div>
      {isActionable(status, meetingViaCalendly, displayStatus) ? (
        <CalloutStatus>{displayStatus}</CalloutStatus>
      ) : completedAt ? (
        <CompletedOnStatus date={completedAt} />
      ) : (
        <PendingStatus>{displayStatus}</PendingStatus>
      )}
    </div>
  )
}

const FirstRevisionRow = () => {
  const { coachRevisions, status, dueAt, noShow } = useResumeReview()

  const isFinalRevision = coachRevisions?.length === 1 && status === 'closed'
  const currentCoachRevision = isFinalRevision ? {} : coachRevisions?.[0] || {}

  const showDueAt = dueAt && !coachRevisions?.length && !noShow
  return (
    <RevisionRow
      revisionNumber={0}
      isFinalRevision={isFinalRevision}
      currentCoachRevision={currentCoachRevision}
      showDueAt={showDueAt}
    >
      First Revision
    </RevisionRow>
  )
}

const SecondRevisionRow = () => {
  const { coachRevisions, status, revisionDeclined, dueAt, coachNextTask } =
    useResumeReview()

  const isFinalRevision = coachRevisions?.length === 2 && status === 'closed'

  const currentCoachRevision =
    isFinalRevision || revisionDeclined ? {} : coachRevisions?.[1] || {}

  const numCoachRevisions = revisionDeclined
    ? coachRevisions?.length + 1
    : coachRevisions?.length

  const showDueAt =
    dueAt &&
    numCoachRevisions === 1 &&
    status !== 'returned' &&
    coachNextTask !== 'Post Final Document and Close Review'

  return (
    <RevisionRow
      revisionNumber={1}
      isFinalRevision={isFinalRevision}
      currentCoachRevision={currentCoachRevision}
      showDueAt={showDueAt}
    >
      Second Revision
    </RevisionRow>
  )
}

const FinalRevisionRow = () => {
  const { coachRevisions, status, dueAt, revisionDeclined, coachNextTask } =
    useResumeReview()
  const currentCoachRevision =
    status === 'closed' ? coachRevisions?.[coachRevisions?.length - 1] : {}

  const numCoachRevisions = revisionDeclined
    ? coachRevisions?.length + 1
    : coachRevisions?.length

  const showDueAt =
    dueAt &&
    (numCoachRevisions === 2 ||
      coachNextTask === 'Post Final Document and Close Review')

  return (
    <RevisionRow
      revisionNumber={2}
      isFinalRevision={true}
      currentCoachRevision={currentCoachRevision}
      showDueAt={showDueAt}
    >
      Final Revision
    </RevisionRow>
  )
}

const RevisionRow = ({
  revisionNumber,
  isFinalRevision,
  currentCoachRevision,
  showDueAt = false,
  children,
}) => {
  const { dueAt, revisionDeclined, id: serviceRequestId } = useResumeReview()

  const isActionable = useIsActionable({ revisionNumber })

  const invoiceableMins = currentCoachRevision?.invoicedMinutes
  const invoiceId = currentCoachRevision?.invoiceId
  const invoiceMinuteItemId = currentCoachRevision?.invoiceMinuteItemId
  const showInvoiceLink = currentCoachRevision?.showInvoiceLink

  const actionableText = isFinalRevision
    ? 'Upload Final and Close Review'
    : 'Upload and Provide Notes'

  const currentRevisionDueAt = showDueAt
    ? adjustDueAtToWeekday(dueAt)?.format('MM/DD/YYYY')
    : null

  const hasCoachRevision = Object.keys(currentCoachRevision)?.length > 0

  const otherDocuments = currentCoachRevision?.otherDocuments || []
  const hasOtherDocuments = otherDocuments.length > 0
  const showDocuments = hasOtherDocuments || currentCoachRevision?.url

  const effectiveRevisionNumber =
    revisionDeclined && revisionNumber == 2 ? 1 : revisionNumber

  const showCompletedStatus = currentCoachRevision?.createdAt
  const showPendingStatus = revisionNumber == 1 && revisionDeclined

  return (
    <StripedRow>
      <TableCell className="text-medium">{children}</TableCell>
      <TableCell>
        {showCompletedStatus ? (
          <CompletedOnStatus completedAt={currentCoachRevision.createdAt} />
        ) : showPendingStatus ? (
          <PendingStatus>Declined</PendingStatus>
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {showDueAt ? currentRevisionDueAt : <span>&mdash;</span>}
      </TableCell>
      <TableCell>
        {showDocuments ? (
          <DocumentsCol
            currentCoachRevision={currentCoachRevision}
            otherDocuments={otherDocuments}
            hasOtherDocuments={hasOtherDocuments}
          />
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-2">
          {hasCoachRevision && (
            <RevisionDialog
              type="Coach"
              revisionNumber={effectiveRevisionNumber}
            >
              Coach Feedback
            </RevisionDialog>
          )}
          {isActionable && (
            <UploadResumeRevision
              revisionNumber={revisionNumber}
              isFinalRevision={isFinalRevision}
            >
              {actionableText}
            </UploadResumeRevision>
          )}
          {!isActionable && !hasCoachRevision && !serviceRequestId && (
            <RequestCell revisionNumber={revisionNumber}>
              <UploadResumeRevision createRequest={true}>
                Initiate Request and Provide Feedback
              </UploadResumeRevision>
            </RequestCell>
          )}
        </div>
      </TableCell>
      <TableCell>
        {invoiceableMins ? (
          showInvoiceLink ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
            >
              {invoiceableMins}
            </a>
          ) : (
            <span
              className="cursor-not-allowed text-gray-400"
              title="You do not have access to this invoice."
            >
              {invoiceableMins}
            </span>
          )
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
    </StripedRow>
  )
}

export const DocumentsCol = ({
  currentCoachRevision,
  otherDocuments,
  hasOtherDocuments,
  preWork,
}) => {
  const url = currentCoachRevision?.url || preWork?.url
  const name = currentCoachRevision ? 'Coach Revision' : 'Pre-Work Document'
  return (
    <>
      {url && <a href={url}>{name}</a>}
      {hasOtherDocuments && (
        <div className="mt-2 flex flex-col gap-1">
          {otherDocuments.map((doc, idx) => (
            <a key={`other-doc-${idx}`} href={doc.url}>
              {doc.name}
            </a>
          ))}
        </div>
      )}
    </>
  )
}

const SecondMeetingRow = ({ revisionNumber }) => {
  const { candidateRevisions } = useResumeReview()
  const currentResumeReview = useResumeReview()
  const formatTime = useFormatTime()

  const candidateRevision = candidateRevisions?.[revisionNumber] || {}
  const {
    completedEvents = [],
    pendingEvents = [],
    meetingViaCalendly = false,
    url,
  } = candidateRevisions?.[0] || {}

  const hasPendingMeeting = pendingEvents.length > 0
  const hasCompletedEvent = completedEvents.length > 0
  const hasCandidateRevision = Object.keys(candidateRevision).length > 0

  if (meetingViaCalendly) {
    const currentCompletedEvent = completedEvents[completedEvents.length - 1]
    const currentPendingEvent = pendingEvents[pendingEvents.length - 1]

    const displayCompletedTime = currentCompletedEvent
      ? formatTime(currentCompletedEvent?.endTime, 'short')
      : null
    const scheduledForTime = currentPendingEvent
      ? formatTime(currentPendingEvent?.startTime)
      : null
    const scheduledForDate = currentPendingEvent
      ? formatTime(currentPendingEvent?.startTime, 'short')
      : null

    return (
      <StripedRow>
        <TableCell className="text-medium">Second Revision Request</TableCell>
        <TableCell>
          {hasCompletedEvent ? (
            <CompletedOnStatus completedAt={displayCompletedTime} />
          ) : hasPendingMeeting ? (
            <CompletedStatus>Scheduled for {scheduledForTime}</CompletedStatus>
          ) : (
            <PendingStatus>Awaiting Candidate</PendingStatus>
          )}
        </TableCell>
        <TableCell>{scheduledForDate || <span>&mdash;</span>}</TableCell>
        <TableCell>{url && <a href={url}>Candidate Resume</a>}</TableCell>
        <TableCell>
          <div className="mb-1 flex flex-col gap-1">
            {hasCandidateRevision ? (
              <>
                <RevisionDialog
                  type="Candidate"
                  revisionNumber={revisionNumber}
                >
                  View Request
                </RevisionDialog>
                <CoachChangeFormatDialog
                  updateCandidateRevision={true}
                  currentCandidateRevision={candidateRevision}
                />
              </>
            ) : (
              <span>&mdash;</span>
            )}
          </div>
          <MeetingInfoCell />
        </TableCell>
        <TableCell>&mdash;</TableCell>
      </StripedRow>
    )
  }

  // Non-Calendly fallback cases
  let statusMessage = 'Not Started'
  if (currentResumeReview.status === 'returned') {
    statusMessage = 'Awaiting Candidate'
  } else if (candidateRevision.meetingViaCalendly === false) {
    statusMessage = 'No Meeting Required'
  }

  return (
    <StripedRow>
      <TableCell className="text-medium">Second Revision Request</TableCell>
      <TableCell>
        <PendingStatus>{statusMessage}</PendingStatus>
      </TableCell>
      <TableCell>
        <span>&mdash;</span>
      </TableCell>
      <TableCell>
        {url ? <a href={url}>Candidate Resume</a> : <span>&mdash;</span>}
      </TableCell>
      <TableCell>
        <div className="mb-1 flex flex-col gap-1">
          {hasCandidateRevision ? (
            <>
              <RevisionDialog type="Candidate" revisionNumber={revisionNumber}>
                View Request
              </RevisionDialog>
              <CoachChangeFormatDialog
                updateCandidateRevision={true}
                currentCandidateRevision={candidateRevision}
              />
            </>
          ) : (
            <span>&mdash;</span>
          )}
        </div>
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const PreWorkRow = () => {
  const { preWork, meetingViaCalendly } = useResumeReview()
  const isActionable = !preWork

  const invoiceableMins = preWork?.invoicedMinutes
  const invoiceId = preWork?.invoiceId
  const invoiceMinuteItemId = preWork?.invoiceMinuteItemId

  const otherDocuments = preWork?.otherDocuments || []
  const hasOtherDocuments = otherDocuments.length > 0
  const showDocuments = hasOtherDocuments || preWork?.url
  const formatTime = useFormatTime()

  const documentsCell = showDocuments ? (
    <DocumentsCol
      preWork={preWork}
      otherDocuments={otherDocuments}
      hasOtherDocuments={hasOtherDocuments}
    />
  ) : (
    'N/A'
  )

  const preWorkDueAt = preWork ? (
    formatTime(preWork.createdAt, 'short')
  ) : (
    <span>&mdash;</span>
  )

  const displayCompletedTime = preWork?.createdAt
    ? formatTime(preWork.createdAt, 'short')
    : null
  if (!meetingViaCalendly && !preWork) return null
  return (
    <StripedRow>
      <TableCell className="text-medium">Optional Pre-Work</TableCell>
      <TableCell>
        {preWork ? (
          <CompletedOnStatus completedAt={displayCompletedTime} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>{preWorkDueAt}</TableCell>
      <TableCell>{documentsCell}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-2">
          {preWork && (
            <RevisionDialog type="Pre-Work" preWork={preWork}>
              View Pre-Work
            </RevisionDialog>
          )}
          {isActionable && (
            <UploadResumeRevision type="Pre-Work">
              Upload Pre-Work
            </UploadResumeRevision>
          )}
        </div>
      </TableCell>
      <TableCell>
        {(
          <a
            target="_blank"
            rel="noreferrer"
            href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
          >
            {invoiceableMins}
          </a>
        ) || <span>&mdash;</span>}
      </TableCell>
    </StripedRow>
  )
}

const MeetingInfoCell = () => {
  const currentResumeReview = useResumeReview()
  const { completedEvents, pendingEvents, coachRevisions, candidateId } =
    useResumeReview()

  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]

  // event has happened but there are no revisions, meaning perhaps they never met
  const showNoShowDialog = currentCompletedEvent && !coachRevisions?.length

  const invalidateQueryCache = () => {
    queryClient.invalidateQueries(['serviceRequests', candidateId])
  }

  const { joinUrl } = currentPendingEvent || {}

  if (showNoShowDialog)
    return (
      <NoShowDialog calendlyEvent={currentCompletedEvent}>
        Mark as No-Show
      </NoShowDialog>
    )
  if (currentPendingEvent) {
    return (
      <div className="flex flex-col gap-1">
        <a href={joinUrl} target="_blank" rel="noopener noreferrer">
          Join Meeting
        </a>
        <CancelMeetingModal
          serviceRequest={currentResumeReview}
          onSuccess={invalidateQueryCache}
        >
          Cancel & Message Candidate
        </CancelMeetingModal>
        <NoShowDialog calendlyEvent={currentPendingEvent}>
          Mark as No-Show
        </NoShowDialog>
      </div>
    )
  }
  return null
}

const NoShowRows = ({ idx }) => {
  const { cancelledOrNoShowEvents, candidateRevisions, pendingEvents } =
    useResumeReview()
  const currentCandidateRevision = candidateRevisions?.[0] || {}
  const relevantEvents =
    idx === 0
      ? cancelledOrNoShowEvents
      : currentCandidateRevision?.cancelledOrNoShowEvents
  if (!relevantEvents || pendingEvents.length > 0) return null

  return (
    <>
      {relevantEvents.map((event, eventIdx) => {
        const issue = event.cancelled
          ? 'Cancelled'
          : event.rescheduled
            ? 'Rescheduled'
            : 'No-Show'
        const invoiceableMins = event?.invoicedMinutes
        const invoiceId = event?.invoiceId
        const invoiceMinuteItemId = event?.invoiceMinuteItemId
        return (
          <StripedRow key={`no-show-${idx}-${eventIdx}`}>
            <TableCell className="text-medium">Meeting Issue</TableCell>
            <TableCell>
              <CalloutStatus>{issue}</CalloutStatus>
            </TableCell>
            <TableCell>&mdash;</TableCell>
            <TableCell>&mdash;</TableCell>
            <TableCell>
              {event.cancelled ? (
                <RescheduleMeetingDialog event={event}>
                  <Button
                    variant="link"
                    className="justify-start font-normal text-intoo-blue-medium hover:text-link"
                  >
                    Reschedule Meeting
                  </Button>
                </RescheduleMeetingDialog>
              ) : (
                <span>&mdash;</span>
              )}
            </TableCell>
            <TableCell>
              {invoiceableMins ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
                >
                  {invoiceableMins}
                </a>
              ) : (
                <span>&mdash;</span>
              )}
            </TableCell>
          </StripedRow>
        )
      })}
    </>
  )
}

const InitialMeetingRow = () => {
  const { completedEvents, pendingEvents, meetingViaCalendly } =
    useResumeReview()
  const formatTime = useFormatTime()

  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]
  const displayCompletedTime = currentCompletedEvent
    ? formatTime(currentCompletedEvent?.endTime, 'short')
    : null
  const scheduledForTime = currentPendingEvent
    ? formatTime(currentPendingEvent?.startTime)
    : null
  const scheduledForDate = currentPendingEvent
    ? formatTime(currentPendingEvent?.startTime, 'short')
    : null

  if (!meetingViaCalendly) {
    return (
      <StripedRow>
        <TableCell className="text-medium">Initial Meeting Info</TableCell>
        <TableCell>
          <PendingStatus>N/A</PendingStatus>
        </TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
      </StripedRow>
    )
  }

  return (
    <StripedRow>
      <TableCell className="text-medium">Initial Meeting Info</TableCell>
      <TableCell>
        {completedEvents?.length > 0 ? (
          <CompletedOnStatus completedAt={displayCompletedTime} />
        ) : pendingEvents?.length > 0 ? (
          <CompletedStatus>Scheduled for {scheduledForTime}</CompletedStatus>
        ) : (
          <PendingStatus>Awaiting Candidate</PendingStatus>
        )}
      </TableCell>
      <TableCell>{scheduledForDate || <span>&mdash;</span>}</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        <MeetingInfoCell />
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const StripedRow = ({ children, className, ...props }) => {
  return (
    <TableRow
      {...props}
      className={cn(
        'odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}

const ReviewFormatRow = () => {
  const { createdAt, meetingViaCalendly, coachRevisions } = useResumeReview()
  const reviewStarted = Boolean(createdAt)
  const currentType = reviewStarted
    ? meetingViaCalendly
      ? 'Meeting Based'
      : 'No Meeting Required'
    : 'Not Started'
  return (
    <StripedRow>
      <TableCell className="text-medium">Resume Review Format</TableCell>
      <TableCell>
        {reviewStarted ? (
          <div className="font-sm text-medium max-w-max rounded-lg bg-[#E9F2E2] px-4 py-2 text-intoo-green-coachcta">
            {currentType}
          </div>
        ) : (
          <PendingStatus>{currentType}</PendingStatus>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        {reviewStarted && coachRevisions?.length === 0 ? (
          <CoachChangeFormatDialog />
        ) : null}
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const CreatedAtRow = () => {
  const { createdAt, coachResumeUrl } = useResumeReview()
  return (
    <StripedRow>
      <TableCell className="text-medium">Resume Review Created</TableCell>
      <TableCell>
        {createdAt ? (
          <CompletedOnStatus completedAt={createdAt} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        {createdAt && coachResumeUrl ? (
          <a href={coachResumeUrl} target="_blank" rel="noreferrer">
            Candidate Resume
          </a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        {createdAt ? (
          <RevisionDialog type="Candidate">View Request</RevisionDialog>
        ) : (
          <RequestCell>
            <div className="flex flex-col gap-2">
              <InitiateServiceRequestDialog
                requestType="ResumeReview"
                initiationMode="electronic"
              >
                <Button
                  variant="link"
                  className="justify-start font-normal text-intoo-blue-medium hover:text-link"
                >
                  Open Platform Review
                </Button>
              </InitiateServiceRequestDialog>
              <InitiateServiceRequestDialog
                requestType="ResumeReview"
                initiationMode="meeting"
              >
                <Button
                  variant="link"
                  className="justify-start font-normal text-intoo-blue-medium hover:text-link"
                >
                  Schedule Meeting For Candidate
                </Button>
              </InitiateServiceRequestDialog>
            </div>
          </RequestCell>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const AssignmentRow = () => {
  const { assignedAt, assignmentDueDate, coachId, currentUserIsCoach } =
    useResumeReview()
  const formatTime = useFormatTime()
  const displayAssignmentDue = assignmentDueDate
    ? formatTime(assignmentDueDate, 'short')
    : null
  return (
    <StripedRow>
      <TableCell className="text-medium">Resume Review Assigned</TableCell>
      <TableCell>
        {assignedAt ? (
          <CompletedOnStatus completedAt={assignedAt} />
        ) : currentUserIsCoach ? (
          <CompletedStatus>Assigned to You</CompletedStatus>
        ) : coachId ? (
          <PendingStatus>Assigned to Another Coach</PendingStatus>
        ) : (
          <PendingStatus>Incomplete</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {assignedAt ? <span>&mdash;</span> : displayAssignmentDue}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>{assignedAt ? 5 : <span>&mdash;</span>}</TableCell>
    </StripedRow>
  )
}

export const PendingStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-[#87898A] px-4 py-2 text-white">
      {children}
    </div>
  )
}

export const CompletedOnStatus = ({ completedAt }) => {
  const formatTime = useFormatTime()
  const displayDate = formatTime(completedAt, 'short')
  if (!completedAt) {
    return <CompletedStatus>Completed</CompletedStatus>
  }
  return <CompletedStatus>Completed on {displayDate}</CompletedStatus>
}

export const CompletedStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-[#E9F2E2] px-4 py-2 text-intoo-green-coachcta">
      {children}
    </div>
  )
}

const _ActionableStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-intoo-green-coachcta px-4 py-2 text-[#E9F2E2]">
      {children}
    </div>
  )
}

export const CalloutStatus = ({ children }) => {
  return (
    <div className="font-sm text-medium max-w-max rounded-lg bg-yellow-100 px-4 py-2 text-yellow-700">
      {children}
    </div>
  )
}

const ReviewHeading = () => {
  const cells = [
    'Service Progress',
    'Status',
    'Due Date',
    'Documents',
    'Actions',
    'Invoiced Time (minutes)',
  ]
  return (
    <TableHeader className="bg-intoo-blue-medium-foo h-6">
      <TableRow className="bg-intoo-blue-medium text-lg hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}
